import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";
import {
  useFoodicsSuppliers,
  useImportFoodicsSuppliers,
} from "src/api/integrations/foodics";
import { notificationsState } from "src/store/notifications";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import { NoData } from "src/shared/components/tables/no-data";

type SuppliersStepProps = {
  onContinue: () => void;
};

export const SuppliersStep = ({ onContinue }: SuppliersStepProps) => {
  const { t } = useTranslation("integrations");
  const { t: generalT } = useTranslation("general");
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(50);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Ids already defaulted to selected once — unchecking must survive page changes.
  const seenIdsRef = useRef<Set<string>>(new Set());

  const { data: suppliersResponse, isLoading } = useFoodicsSuppliers({
    page: page + 1,
    per_page: perPage,
  });
  const { mutate: importSuppliers, status: importStatus } =
    useImportFoodicsSuppliers();

  const suppliers = suppliersResponse?.data?.suppliers ?? [];
  const pagination = suppliersResponse?.data?.pagination ?? null;
  const totalCount = pagination?.total ?? suppliers.length;

  // All suppliers are selected by default (screenshot behavior)
  useEffect(() => {
    const newIds = suppliers
      .map((supplier) => supplier.external_id)
      .filter(
        (id): id is string => Boolean(id) && !seenIdsRef.current.has(id!)
      );
    if (newIds.length > 0) {
      newIds.forEach((id) => seenIdsRef.current.add(id));
      setSelectedIds((prev) => [...prev, ...newIds]);
    }
  }, [suppliers]);

  useEffect(() => {
    if (importStatus === "success") {
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: t("foodicsWorkspace.editImport.suppliersImportSuccess"),
        },
      ]);
      onContinue();
    } else if (importStatus === "error") {
      setNotifications([
        ...notifications,
        { type: "error", message: t("menuImport.importError") },
      ]);
    }
  }, [importStatus]);

  const toggleSupplier = (id: string | null) => {
    if (!id) return;
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const selectableVisibleIds = suppliers
    .map((supplier) => supplier.external_id)
    .filter((id): id is string => Boolean(id));
  const allVisibleSelected =
    selectableVisibleIds.length > 0 &&
    selectableVisibleIds.every((id) => selectedIds.includes(id));
  const someVisibleSelected = selectableVisibleIds.some((id) =>
    selectedIds.includes(id)
  );

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) =>
        prev.filter((id) => !selectableVisibleIds.includes(id))
      );
      return;
    }
    setSelectedIds((prev) => [
      ...prev,
      ...selectableVisibleIds.filter((id) => !prev.includes(id)),
    ]);
  };

  const handleImportAndContinue = () => {
    if (selectedIds.length === 0) {
      onContinue();
      return;
    }
    importSuppliers({ external_ids: selectedIds });
  };

  return (
    <Stack gap={2}>
      <Box>
        <Typography fontSize={16} fontWeight={600} color="#101828">
          {t("foodicsHub.suppliersCardTitle")}
        </Typography>
        <Typography fontSize={12} color="#667085">
          {t("foodicsWorkspace.editImport.suppliersHint")}
        </Typography>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allVisibleSelected}
                  indeterminate={someVisibleSelected && !allVisibleSelected}
                  onChange={toggleSelectAllVisible}
                  disabled={selectableVisibleIds.length === 0}
                />
              </TableCell>
              {[
                t("menuImport.table.name"),
                generalT("email"),
                t("menuImport.table.phone"),
                t("menuImport.table.taxNumber"),
              ].map((item) => (
                <TableCell
                  key={item}
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    lineHeight: "18px",
                    color: "#475467",
                    bgcolor: "#F9FAFB",
                  }}
                >
                  {item}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {isLoading ? (
            <TableLoadingSkeleton rowsLength={5} cellsLength={5} />
          ) : (
            <TableBody>
              {suppliers.map((supplier, index) => (
                <TableRow
                  key={supplier.external_id ?? supplier.name ?? `sup-${index}`}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={
                        !!supplier.external_id &&
                        selectedIds.includes(supplier.external_id)
                      }
                      onChange={() => toggleSupplier(supplier.external_id)}
                      disabled={!supplier.external_id}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography fontSize={14} fontWeight={500}>
                        {supplier.name ?? "—"}
                      </Typography>
                      {supplier.already_imported && (
                        <Chip
                          size="small"
                          color="success"
                          label={t("menuImport.imported")}
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>{supplier.email ?? "—"}</TableCell>
                  <TableCell>{supplier.phone ?? "—"}</TableCell>
                  <TableCell>{supplier.supplier_tax_number ?? "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
        {!isLoading && suppliers.length === 0 && <NoData />}
      </TableContainer>

      <TablePagination
        component="div"
        labelRowsPerPage={generalT("rowsPerPage")}
        count={totalCount}
        page={Math.max((pagination?.current ?? page + 1) - 1, 0)}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={pagination?.per ?? perPage}
        onRowsPerPageChange={(event) => {
          const value = parseInt(event.target.value, 10);
          setPerPage(value);
          setPage(0);
        }}
        rowsPerPageOptions={[25, 50, 100, 200]}
      />

      {/* Footer */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
        pt={2}
        borderTop="1px solid #EAECF0"
      >
        <Typography fontSize={14} color="#667085">
          {t("foodicsWorkspace.editImport.selectedOf", {
            selected: selectedIds.length,
            total: totalCount,
          })}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" color="tertiary" disabled>
            {generalT("back")}
          </Button>
          <Button
            variant="contained"
            disabled={importStatus === "pending"}
            startIcon={
              importStatus === "pending" ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
            onClick={handleImportAndContinue}
          >
            {t("foodicsWorkspace.editImport.importAndContinue")}
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
};
